"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"

/** The API's page-view counter, on the site's own address (issue #111). */
export const VISITS_ENDPOINT = "/api/v1/visits"

/**
 * The staff sign-in cookie (the same name as `AUTH_COOKIE` in `lib/authFetch.ts`,
 * repeated here so the public pages don't load the admin fetch helpers).
 */
const STAFF_COOKIE = "authToken"

/** True when this browser holds a non-empty staff sign-in cookie. */
export function staffSignedIn(cookies: string): boolean {
    return cookies
        .split(";")
        .map((part) => part.trim())
        .some((part) => part.startsWith(`${STAFF_COOKIE}=`) && part.length > STAFF_COOKIE.length + 1)
}

/** False for the admin area and the sign-in page, which are never counted. */
export function countedPath(path: string): boolean {
    return !["/admin", "/login"].some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

/**
 * Tells the site's own server that a public page opened, once per page view
 * (also on client-side navigations). It needs no cookie consent because it
 * sends only the path and identifies no one: `credentials: "omit"` keeps every
 * cookie out of the request, and nothing is stored in the browser. It sends
 * nothing when a staff member is signed in in this browser, and never from
 * `/admin` or `/login`. Errors and `429` answers are ignored.
 */
export default function PageViewBeacon() {
    const pathname = usePathname()
    // The last path sent: React's development double effects and re-renders
    // don't count twice; a navigation to another page does.
    const sent = useRef<string | null>(null)

    useEffect(() => {
        if (!pathname || sent.current === pathname) return
        sent.current = pathname
        if (!countedPath(pathname) || staffSignedIn(document.cookie)) return
        try {
            fetch(VISITS_ENDPOINT, {
                method: "POST",
                body: JSON.stringify({ path: pathname }),
                keepalive: true,
                credentials: "omit",
                headers: { "Content-Type": "application/json" },
            }).catch(() => undefined)
        } catch {
            // A browser without fetch or keepalive: nothing is counted.
        }
    }, [pathname])

    return null
}
